import { Resources } from 'constants/resources';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { required } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { AutocompleteInput, AutocompleteMultiselectInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';
import { cleanEmpty } from 'utils/cleanEmpty';

type DashboardFilterFieldsParams = {
  resetDependentOnChange: boolean;
};

export const useDashboardFilterFields = ({
  resetDependentOnChange,
}: DashboardFilterFieldsParams): FilterFieldConfig[] => {
  const translate = useTypedTranslate();

  const { watch, resetField, setValue } = useFormContext();
  const { onReset, setAppliedFilter, appliedFilters } = useFilterContext();

  const merchantWatched = watch('merchant');
  const providerWatched = watch('provider');

  return [
    {
      name: 'merchant',
      label: translate('app.filter.merchants'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Merchants,
        limitTags: 1,
        clearable: false,
        onApply: () => {
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });

          setAppliedFilter('provider', []);
          setAppliedFilter('gateway', []);
        },
        onReset: () => {
          setValue('provider', appliedFilters?.['provider']);
          setValue('gateway', appliedFilters?.['gateway']);

          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
        },
      },
      input: (
        <ReferenceArrayInput
          filter={{ archive: false }}
          perPage={200}
          reference={Resources.Merchants}
          source="merchant"
        >
          <AutocompleteMultiselectInput
            helperText={false}
            label={translate('app.filter.merchants')}
            name="merchant"
            onChange={
              resetDependentOnChange
                ? () => {
                    resetField('provider', { keepDirty: true });
                    resetField('gateway', { keepDirty: true });
                  }
                : undefined
            }
            optionText="name"
            validate={required()}
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'provider',
      label: translate('app.filter.providers'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Providers,
        limitTags: 1,
        onApply: () => {
          resetField('gateway', { keepDirty: true });
          setAppliedFilter('gateway', []);
        },
        onDelete: () => {
          resetField('gateway', { keepDirty: true });
          setAppliedFilter('gateway', []);
        },
        onReset: () => {
          setValue('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
        },
      },
      input: (
        <ReferenceArrayInput
          filter={cleanEmpty({ merchantAccounts: { merchants: merchantWatched } })}
          perPage={200}
          reference={Resources.Providers}
          source="provider"
        >
          <AutocompleteMultiselectInput
            disabled={!merchantWatched?.length}
            helperText={false}
            label={translate('app.filter.providers')}
            name="provider"
            onChange={
              resetDependentOnChange
                ? () => {
                    resetField('gateway', { keepDirty: true });
                  }
                : undefined
            }
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'gateway',
      label: translate('app.filter.gateways'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Gateways,
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={cleanEmpty({
            merchantAccounts: { merchants: merchantWatched },
            provider: providerWatched,
          })}
          perPage={200}
          reference={Resources.Gateways}
          source="gateway"
        >
          <AutocompleteMultiselectInput
            disabled={!merchantWatched?.length}
            helperText={false}
            label={translate('app.filter.gateways')}
            name="gateway"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'currency',
      label: translate('app.filter.conversionCurrency'),
      chipType: 'text',
      chipProps: {
        clearable: false,
        onDelete: onReset,
      },
      input: (
        <ReferenceInput
          filter={{ active: true }}
          name="currency"
          perPage={1000}
          reference={Resources.Currencies}
          resettable
          resource={Resources.Currencies}
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteInput
            clearOnBlur={false}
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            helperText={false}
            label={translate('app.filter.conversionCurrency')}
            name="currency"
            optionText={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            optionValue="alphaCode"
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
  ];
};
